"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    ChampDate,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { joursAvant } from "@/lib/tools/electronicInvoicing";
import { reprendre } from "@/lib/tools/dossier";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import {
    PLAFONDS,
    PLAFOND_MIXTE,
    SEUILS_TVA,
    plafondProratise,
    statutMicro,
    statutMixteSurDeuxAns,
} from "@/lib/tools/microEnterprise";

/**
 * Vais-je rester en micro-entreprise ?
 *
 * DEUX RÈGLES QUE PRESQUE TOUT LE MONDE ÉCRIT DE TRAVERS.
 *
 * 1. Le régime ne cesse que si le seuil est dépassé DEUX années de suite. La
 *    formule qui circule, « le chiffre d'affaires de N-1 ou N-2 ne doit pas
 *    dépasser le seuil », se lit comme une double condition et fait sortir du
 *    régime des gens qui y sont parfaitement.
 *
 * 2. Le plafond proratisé de la première année ne fait sortir de rien. Le micro
 *    est acquis de plein droit l'année de création ET l'année suivante, faute
 *    de chiffre d'affaires de référence. Le BOFiP donne l'exemple d'une
 *    création au 1er août avec 100 000 € encaissés en cinq mois, soit 238 562 €
 *    ramenés à l'année : micro quand même en N et en N+1.
 *
 * Le prorata sert donc à savoir où l'on met les pieds, jamais à annoncer une
 * sortie. Un simulateur qui affiche « plafond dépassé » à quelqu'un qui vient
 * de créer lui fait peur pour rien, et c'est ce que font les autres.
 *
 * LE VERDICT PORTE SUR LE 1ER JANVIER PROCHAIN, la question que le lecteur se
 * pose vraiment : « vais-je en sortir ? ». Ce 1er janvier se décide sur l'an
 * dernier et sur cette année, dont le chiffre n'est qu'une estimation tant que
 * le 31 décembre n'est pas passé, et l'écran de résultat le dit. Il y a deux
 * ans n'y change rien, et n'est donc pas demandé. Dans la lib, `caN1` désigne
 * la plus récente des deux années regardées et `caN2` la plus ancienne : pour
 * un verdict sur l'an prochain, c'est cette année et l'an dernier.
 *
 * LE PARCOURS SE RAMIFIE SUR L'ANCIENNETÉ. Créé cette année : une date, et le
 * plafond de repère répond en direct. Créé l'an dernier : rien à calculer, le
 * régime est acquis, et l'on montre le plafond qui comptera. Deux ans ou plus :
 * les deux années regardées, curseur par curseur. En activité mixte, chaque
 * année se lit sur ses deux conditions ; un affilié Cipav, lui, n'en a qu'une,
 * 83 600 € sur le total de ses deux parts, et la lib la porte déjà sous
 * l'activité « cipav » : on lui demande son total, pas ses parts.
 */

const PHASES = {
    anciennete: 0,
    activite: 0,
    cipav: 0,
    dateDebut: 1,
    caN1: 1,
    caN: 1,
    mixteVenteN1: 1,
    mixteServicesN1: 1,
    mixteVenteN: 1,
    mixteServicesN: 1,
    resultat: 2,
};

const L = {
    fr: {
        phases: ["Votre entreprise", "Vos chiffres", "Votre régime"],
        retour: "Retour",
        continuer: "Continuer",
        voirResultat: "Voir mon résultat",
        recommencer: "Refaire la simulation",
        modifier: "Modifier mes réponses",

        qAnciennete: "Où en est votre activité ?",
        qAncienneteAide:
            "Le régime micro est acquis de plein droit, c'est-à-dire automatiquement et sans condition de chiffre d'affaires, l'année de création et l'année suivante. Selon votre ancienneté, la question du plafond ne se pose donc pas encore, ou se pose sur l'an dernier et cette année.",
        premiere: "Je crée cette année",
        premiereDetail: "Activité déjà déclarée, ou sur le point de l'être",
        deuxieme: "J'ai créé l'an dernier",
        deuxiemeDetail: "Vous vivez votre première année civile complète",
        etablie: "J'ai créé il y a deux ans ou plus",
        etablieDetail: "L'an dernier était déjà une année civile complète",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Le plafond dépend d'elle : {vente} pour la vente de marchandises, {services} pour les prestations et les activités libérales. En activité mixte, les deux s'appliquent en même temps.",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, hôtellerie, restauration",
        services: "Prestations ou activité libérale",
        servicesDetail: "Bâtiment, conseil, création, soins, meublés et chambres d'hôtes",
        mixte: "Les deux à la fois",
        mixteDetail:
            "Vous vendez des marchandises et vous facturez des prestations, comme un artisan qui fournit et pose",

        qCipav: "Êtes-vous affilié à la Cipav ?",
        qCipavAide:
            "La Cipav est la caisse de retraite des professions libérales réglementées : architecte, psychologue, ostéopathe, guide-conférencier. Pour ses affiliés, le plafond de {services} porte sur le total des deux parts, vente comprise, et non sur la seule part prestations.",
        cipavOui: "Oui, je relève de la Cipav",
        cipavOuiDetail: "Profession libérale réglementée, retraite versée à la Cipav",
        cipavNon: "Non, je relève du régime général",
        cipavNonDetail: "Artisan, commerçant ou libéral non réglementé : le cas le plus courant",

        qDate: "À quelle date démarre votre activité ?",
        qDateAide:
            "Celle déclarée au guichet unique, le portail de l'INPI où l'on immatricule son entreprise, ou celle que vous prévoyez. Elle fixe le plafond auquel vous pouvez comparer votre première année, réduit au prorata des jours qui vous séparent du 31 décembre.",
        dateHorsAnnee:
            "Indiquez une date de {annee} : le plafond de repère se calcule sur vos jours d'existence dans l'année en cours.",
        prorataDirect:
            "Votre plafond de repère cette année : {plafond}, soit {seuil} ramenés à {jours} {existence} en {annee}.",
        prorataMixteDirect:
            "Vos plafonds de repère cette année : {global} au global, dont {services} pour la part prestations, soit vos deux plafonds ramenés à {jours} {existence} en {annee}.",
        existence: "jours d'existence",
        existencePrevus: "jours d'existence prévus",
        prorataCapital:
            "Dépasser ce prorata cette année est sans conséquence : le régime micro vous est acquis cette année et l'année prochaine, quel que soit votre chiffre d'affaires.",

        qCaN1: "Combien avez-vous encaissé l'an dernier ?",
        qCaN1Aide:
            "Le chiffre d'affaires réellement encaissé, hors taxes, et non simplement facturé : une facture de décembre payée en janvier compte sur l'année suivante. C'est la première des deux années qui décident de votre régime de l'an prochain.",
        qCaN: "Et cette année ?",
        qCaNAide:
            "Ce que vous avez encaissé à ce jour, complété par ce que vous prévoyez d'ici le 31 décembre : une estimation suffit. C'est la seconde année regardée, et il faut les deux au-dessus du plafond pour perdre le régime : une seule année en dessous suffit à rester.",
        caN1: "Chiffre d'affaires de l'an dernier",
        caN: "Chiffre d'affaires de cette année, tel que vous l'estimez",
        caTotalN1: "Total encaissé l'an dernier, vente et prestations",
        caTotalN: "Total estimé cette année, vente et prestations",
        caAide: "Déplacez le curseur : la lecture se met à jour.",
        sousSeuilN1: "Sous le plafond de {seuil} : l'an dernier compte comme une année conforme.",
        surSeuilN1:
            "Au-dessus du plafond de {seuil} : l'an dernier compte pour un dépassement, qui, à lui seul, ne vous fait pas sortir du régime.",
        sousSeuilN:
            "Sous le plafond de {seuil} : si vous terminez l'année ainsi, elle compte comme une année conforme.",
        surSeuilN:
            "Au-dessus du plafond de {seuil} : si vous terminez l'année ainsi, elle compte pour un dépassement, qui, à lui seul, ne vous fait pas sortir du régime.",

        qMixteVenteN1: "L'an dernier, combien avez-vous encaissé en vente de marchandises ?",
        qMixteVenteN1Aide:
            "Nous séparons vos deux parts parce que deux plafonds s'appliquent en même temps : {global} sur le total, dont {services} au plus pour les prestations. L'an dernier est la première des deux années qui décident de votre régime de l'an prochain.",
        qMixteServicesN1: "Et en prestations de services, l'an dernier ?",
        qMixteServicesN1Aide:
            "C'est la part qui fait sortir le plus souvent : elle peut dépasser {services} alors que le total reste très en dessous de {global}.",
        qMixteVenteN: "Cette année, combien encaissez-vous en vente de marchandises ?",
        qMixteVenteNAide:
            "Encaissé à ce jour, complété par ce que vous prévoyez d'ici le 31 décembre : une estimation suffit. Cette année est la seconde des deux années regardées, et il faut les deux hors des conditions pour perdre le régime.",
        qMixteServicesN: "Et en prestations de services, cette année ?",
        qMixteServicesNAide:
            "La part prestations de cette année, comparée à son plafond de {services} : c'est elle qui dit si les deux années sortent des conditions, et donc si le régime cesse l'an prochain.",
        partVente: "Part vente de marchandises",
        partServices: "Part prestations de services",
        mixteAnneeOk: "Les deux conditions sont respectées cette année-là.",
        mixteAnneeGlobal: "Le total dépasse {global} : la condition globale n'est pas respectée.",
        mixteAnneeServices:
            "La part prestations dépasse {services} : cela suffit à ne pas respecter les conditions, même si le total reste sous {global}.",
        mixteAnneeDeux:
            "Le total dépasse {global} et la part prestations dépasse {services} : aucune des deux conditions n'est respectée.",
        mixteDont: "dont {vente} en vente et {services} en prestations",

        resultatTitre: "Votre régime micro",
        anDernier: "L'an dernier",
        cetteAnnee: "Cette année, estimée",
        sous: "sous le plafond",
        dessus: "au-dessus du plafond",
        dessusIsole: "au-dessus du plafond, dépassement isolé",
        provisoire:
            "Le chiffre de cette année est une estimation : c'est le montant encaissé au 31 décembre qui comptera.",

        acquisTitre: "Vous restez au micro, quel que soit votre chiffre d'affaires",
        acquisTexte:
            "Vous êtes dans votre année de création : il n'y a pas encore de chiffre d'affaires de référence pour remettre le régime en cause, ni cette année ni l'an prochain. Même en dépassant largement le plafond, vous restez micro-entrepreneur.",
        deuxiemeTexte:
            "L'an dernier était votre année de création et celle-ci est la suivante : le régime vous est acquis pour les deux, quel que soit votre chiffre d'affaires. L'an prochain, l'administration regardera l'an dernier et cette année, et il faudrait avoir dépassé le plafond les deux fois pour sortir.",
        deuxiemeProrata:
            "Pour l'année de création, ce plafond est réduit au prorata de vos jours d'existence, un ajustement que nous n'appliquons pas ici.",
        plafondTitre: "Le plafond qui comptera pour vous",
        plafondTexte:
            "Chaque année civile se compare à ce montant : votre régime de l'an prochain dépend de l'an dernier et de cette année.",
        plafondMixteTexte:
            "Sur le total de vos deux parts, dont {services} au plus pour les prestations : chaque année civile se lit sur ces deux conditions.",
        prorataTitre: "Votre plafond de repère cette année",
        prorataTexte:
            "Soit {seuil} ramenés à vos {jours} {existence} en {annee}. Ce montant vous sert seulement de repère pour situer votre activité.",
        prorataMixteTexte:
            "Au global, dont {services} pour la part prestations : vos deux plafonds ramenés à {jours} {existence} en {annee}. Ces montants vous servent seulement de repères pour situer votre activité.",
        mixteRappel:
            "En activité mixte, deux conditions seront regardées en même temps le jour où vos années compteront : {global} au global, dont {services} au plus pour la part prestations.",
        cipavRappel:
            "Affilié Cipav, vous n'avez qu'une condition : {seuil} sur le total de vos deux parts, vente comprise.",

        resteTitre: "Vous restez au micro l'an prochain",
        resteTexte:
            "Le régime ne cesse que si le plafond de {seuil} est dépassé deux années de suite. L'an dernier comme cette année, vous êtes en dessous : le régime micro vous est acquis au 1er janvier {prochaine}.",
        resteMarge: "Cette année, vous êtes {marge} sous le plafond.",
        resteSuivant:
            "L'année d'après ne dépendra que de cette année et de l'an prochain : une seule année sous le plafond suffit à rester.",

        unSeulTitre: "Un seul dépassement : vous restez au micro",
        unSeulN1Texte:
            "Vous avez dépassé {seuil} l'an dernier, mais cette année vous repassez en dessous. Une seule année sous le plafond remet le compteur à zéro : vous restez au micro au 1er janvier {prochaine}, et l'année d'après aussi, quel que soit votre chiffre d'affaires de l'an prochain.",
        unSeulNTexte:
            "Vous dépassez {seuil} cette année, mais pas l'an dernier. Il faut deux années consécutives au-dessus pour perdre le régime : vous restez au micro au 1er janvier {prochaine}.",
        unSeulNAttention:
            "Attention tout de même : si vous dépassez encore {seuil} l'an prochain, ce sera la deuxième année de suite, et vous sortirez du régime au 1er janvier {suivante}.",

        sortieTitre: "Vous sortez du régime micro au 1er janvier {prochaine}",
        sortieTexte:
            "Le plafond de {seuil} a été dépassé l'an dernier, et il l'est encore cette année : deux années de suite, le régime micro cesse au 1er janvier {prochaine}.",
        sortieSocial:
            "Vous passez au régime réel, celui où l'on déclare son bénéfice réel, et vous perdez en même temps le régime micro-social. Vos cotisations ne se calculent plus sur ce que vous encaissez mais sur ce que vous gagnez, avec un rattrapage l'année suivante : un mois sans encaissement n'est plus un mois sans cotisation.",
        sortieReel:
            "Vous perdez aussi l'abattement forfaitaire : vous déduisez vos charges réelles, et vous devez pouvoir les justifier.",
        sortieRetour:
            "Ce n'est pas définitif. L'année où votre chiffre d'affaires repasse sous le plafond, vous restez au réel, et le régime micro s'applique de nouveau de plein droit l'année suivante.",
        sortieProvisoire:
            "Rien n'est joué avant le 31 décembre : si vous terminez l'année sous {seuil}, le régime tient.",

        resteMixteTexte:
            "L'an dernier comme cette année, vos deux conditions sont respectées : au plus {global} au global, dont au plus {services} en prestations. Le régime micro vous est acquis au 1er janvier {prochaine}.",
        unSeulMixteN1Texte:
            "L'an dernier, l'une des deux conditions n'était pas respectée. Cette année, les deux le sont, et une seule année conforme remet le compteur à zéro : vous restez au micro au 1er janvier {prochaine}.",
        unSeulMixteNTexte:
            "Cette année, l'une des deux conditions n'est pas respectée. L'an dernier, les deux l'étaient, et il faut deux années de suite hors des conditions pour perdre le régime : vous restez au micro au 1er janvier {prochaine}.",
        unSeulMixteAttention:
            "Attention tout de même : si l'une des deux conditions n'est pas respectée l'an prochain non plus, vous sortirez du régime au 1er janvier {suivante}.",
        sortieMixteTexte:
            "L'an dernier comme cette année, l'une des deux conditions n'est pas respectée : deux années de suite, le régime micro cesse au 1er janvier {prochaine}.",
        sortieMixteProvisoire:
            "Rien n'est joué avant le 31 décembre : si vous terminez l'année en respectant les deux conditions, le régime tient.",

        rappels: [
            "Ces seuils valent pour 2026, 2027 et 2028 : ils sont actualisés tous les trois ans, pas chaque année.",
            "Les seuils de TVA sont totalement distincts. Dépasser {tva} en prestations rend redevable de la TVA sans faire perdre la micro-entreprise.",
        ],
    },
    en: {
        phases: ["Your business", "Your figures", "Your scheme"],
        retour: "Back",
        continuer: "Continue",
        voirResultat: "See my result",
        recommencer: "Start over",
        modifier: "Change my answers",

        qAnciennete: "Where does your business stand?",
        qAncienneteAide:
            "The micro scheme applies automatically, with no turnover condition, in the year the business is created and the year after. Depending on where you stand, the ceiling question does not arise yet, or it arises on last year and this year.",
        premiere: "I am starting this year",
        premiereDetail: "Business already registered, or about to be",
        deuxieme: "I started last year",
        deuxiemeDetail: "You are in your first full calendar year",
        etablie: "I started two years ago or more",
        etablieDetail: "Last year was already a full calendar year",

        qActivite: "What is your activity?",
        qActiviteAide:
            "The ceiling depends on it: {vente} for the sale of goods, {services} for services and liberal professions. With mixed activities, both apply at the same time.",
        vente: "Sale of goods",
        venteDetail: "Retail, hotel accommodation, catering",
        services: "Services or liberal profession",
        servicesDetail: "Building, consulting, creative work, care, furnished rentals and guest rooms",
        mixte: "Both at once",
        mixteDetail:
            "You sell goods and you invoice services, like a tradesman who supplies and fits",

        qCipav: "Do you belong to the Cipav?",
        qCipavAide:
            "The Cipav is the pension fund for regulated liberal professions: architects, psychologists, osteopaths, tour guides. For its members, the {services} ceiling applies to the total of both shares, sales included, not just to the services share.",
        cipavOui: "Yes, I belong to the Cipav",
        cipavOuiDetail: "Regulated liberal profession, pension contributions paid to the Cipav",
        cipavNon: "No, I am under the general scheme",
        cipavNonDetail: "Tradesman, trader or unregulated liberal profession: the most common case",

        qDate: "On what date does your business start?",
        qDateAide:
            "The one declared on the guichet unique, the INPI portal where businesses are registered, or the one you are planning. It sets the ceiling you can measure your first year against, reduced pro rata to the days left until 31 December.",
        dateHorsAnnee:
            "Enter a date in {annee}: the benchmark ceiling is computed on the days your business exists in the current year.",
        prorataDirect:
            "Your benchmark ceiling this year: {plafond}, that is {seuil} scaled to the {jours} {existence} in {annee}.",
        prorataMixteDirect:
            "Your benchmark ceilings this year: {global} overall, of which {services} for the services share, that is your two ceilings scaled to the {jours} {existence} in {annee}.",
        existence: "days your business exists",
        existencePrevus: "days your business is expected to exist",
        prorataCapital:
            "Exceeding this pro-rata ceiling this year has no consequence: the micro scheme is yours this year and next, whatever your turnover.",

        qCaN1: "How much did you receive last year?",
        qCaN1Aide:
            "Turnover actually received, excluding VAT, not merely invoiced: a December invoice paid in January counts towards the following year. It is the first of the two years that decide your scheme for next year.",
        qCaN: "And this year?",
        qCaNAide:
            "What you have received so far, plus what you expect until 31 December: an estimate is enough. It is the second year looked at, and both must be over the ceiling to lose the scheme: a single year under is enough to stay.",
        caN1: "Last year's turnover",
        caN: "This year's turnover, as you estimate it",
        caTotalN1: "Total received last year, sales and services",
        caTotalN: "Total estimated this year, sales and services",
        caAide: "Move the slider: the reading updates.",
        sousSeuilN1: "Under the {seuil} ceiling: last year counts as a year within the limit.",
        surSeuilN1:
            "Over the {seuil} ceiling: last year counts as one overrun, which on its own cannot take you out of the scheme.",
        sousSeuilN:
            "Under the {seuil} ceiling: if you end the year like this, it counts as a year within the limit.",
        surSeuilN:
            "Over the {seuil} ceiling: if you end the year like this, it counts as one overrun, which on its own cannot take you out of the scheme.",

        qMixteVenteN1: "Last year, how much did you receive from the sale of goods?",
        qMixteVenteN1Aide:
            "We separate your two shares because two ceilings apply at the same time: {global} on the total, of which {services} at most for services. Last year is the first of the two years that decide your scheme for next year.",
        qMixteServicesN1: "And from services, last year?",
        qMixteServicesN1Aide:
            "It is the share that most often takes people out: it can exceed {services} while the total stays well under {global}.",
        qMixteVenteN: "This year, how much are you receiving from the sale of goods?",
        qMixteVenteNAide:
            "Received so far, plus what you expect until 31 December: an estimate is enough. This year is the second of the two years looked at, and both must fall outside the conditions to lose the scheme.",
        qMixteServicesN: "And from services, this year?",
        qMixteServicesNAide:
            "This year's services share, compared with its {services} ceiling: it decides whether both years fall outside the conditions, and therefore whether the scheme ends next year.",
        partVente: "Sale of goods share",
        partServices: "Services share",
        mixteAnneeOk: "Both conditions are met for that year.",
        mixteAnneeGlobal: "The total exceeds {global}: the overall condition is not met.",
        mixteAnneeServices:
            "The services share exceeds {services}: that alone breaks the conditions, even though the total stays under {global}.",
        mixteAnneeDeux:
            "The total exceeds {global} and the services share exceeds {services}: neither condition is met.",
        mixteDont: "of which {vente} from sales and {services} from services",

        resultatTitre: "Your micro scheme",
        anDernier: "Last year",
        cetteAnnee: "This year, estimated",
        sous: "under the ceiling",
        dessus: "over the ceiling",
        dessusIsole: "over the ceiling, an isolated overrun",
        provisoire:
            "This year's figure is an estimate: only the amount received by 31 December will count.",

        acquisTitre: "You stay in the micro scheme, whatever your turnover",
        acquisTexte:
            "This is your year of creation: there is no reference turnover yet to challenge the scheme, this year or next. Even well over the ceiling, you remain a micro-entrepreneur.",
        deuxiemeTexte:
            "Last year was your year of creation and this one is the year after: the scheme is yours for both, whatever your turnover. Next year, the authority will look at last year and this year, and you would have to exceed the ceiling in both to leave.",
        deuxiemeProrata:
            "For the year of creation, that ceiling is reduced pro rata to the days your business existed, an adjustment we do not apply here.",
        plafondTitre: "The ceiling that will count for you",
        plafondTexte:
            "Each calendar year is measured against this amount: your scheme for next year depends on last year and this year.",
        plafondMixteTexte:
            "On the total of your two shares, of which {services} at most for services: each calendar year is read against these two conditions.",
        prorataTitre: "Your benchmark ceiling this year",
        prorataTexte:
            "That is {seuil} scaled to the {jours} {existence} in {annee}. It only serves as a benchmark to place your business.",
        prorataMixteTexte:
            "Overall, of which {services} for the services share: your two ceilings scaled to the {jours} {existence} in {annee}. They only serve as benchmarks to place your business.",
        mixteRappel:
            "With mixed activities, two conditions will be checked at once from the day your years start counting: {global} overall, of which {services} at most for the services share.",
        cipavRappel:
            "As a Cipav member, you have a single condition: {seuil} on the total of your two shares, sales included.",

        resteTitre: "You stay in the micro scheme next year",
        resteTexte:
            "The scheme only ends if the {seuil} ceiling is exceeded two years running. Last year and this year, you are under it: the micro scheme is yours on 1 January {prochaine}.",
        resteMarge: "This year, you are {marge} under the ceiling.",
        resteSuivant:
            "The year after will depend only on this year and next year: a single year under the ceiling is enough to stay.",

        unSeulTitre: "Only one year over: you stay in the micro scheme",
        unSeulN1Texte:
            "You exceeded {seuil} last year, but this year you are back under it. A single year under the ceiling resets the count: you stay in the micro scheme on 1 January {prochaine}, and the year after too, whatever next year brings.",
        unSeulNTexte:
            "You are exceeding {seuil} this year, but you did not last year. Two consecutive years over are needed to lose the scheme: you stay in the micro scheme on 1 January {prochaine}.",
        unSeulNAttention:
            "One caution, though: if you exceed {seuil} again next year, it will be the second year running, and you will leave the scheme on 1 January {suivante}.",

        sortieTitre: "You leave the micro scheme on 1 January {prochaine}",
        sortieTexte:
            "The {seuil} ceiling was exceeded last year, and it is exceeded again this year: two years running, so the micro scheme ends on 1 January {prochaine}.",
        sortieSocial:
            "You move to the régime réel, the regime where you declare your actual profit, and you lose the micro-social scheme at the same time. Your contributions are no longer calculated on what you receive but on what you earn, with a catch-up the following year: a month without income is no longer a month without contributions.",
        sortieReel:
            "You also lose the flat-rate allowance: you deduct your actual expenses instead, and you must be able to back them up.",
        sortieRetour:
            "It is not permanent. In the year your turnover falls back under the ceiling you stay on the régime réel, and the micro scheme applies again automatically the following year.",
        sortieProvisoire:
            "Nothing is settled before 31 December: if you end the year under {seuil}, the scheme holds.",

        resteMixteTexte:
            "Last year and this year, both of your conditions are met: at most {global} overall, of which at most {services} in services. The micro scheme is yours on 1 January {prochaine}.",
        unSeulMixteN1Texte:
            "Last year, one of the two conditions was not met. This year both are, and a single year within the conditions resets the count: you stay in the micro scheme on 1 January {prochaine}.",
        unSeulMixteNTexte:
            "This year, one of the two conditions is not met. Last year both were, and two consecutive years outside the conditions are needed to lose the scheme: you stay in the micro scheme on 1 January {prochaine}.",
        unSeulMixteAttention:
            "One caution, though: if one of the two conditions is not met next year either, you will leave the scheme on 1 January {suivante}.",
        sortieMixteTexte:
            "Last year and this year, one of the two conditions is not met: two years running, so the micro scheme ends on 1 January {prochaine}.",
        sortieMixteProvisoire:
            "Nothing is settled before 31 December: if you end the year with both conditions met, the scheme holds.",

        rappels: [
            "These thresholds apply to 2026, 2027 and 2028: they are updated every three years, not every year.",
            "VAT thresholds are entirely separate. Passing {tva} in services makes you liable for VAT without costing you the micro scheme.",
        ],
    },
};

/**
 * Une année dépasse-t-elle le plafond ? La comparaison reste dans la lib :
 * posée dans les deux années regardées, une année seule sort si et seulement
 * si elle dépasse. Si la lib changeait sa règle, l'interface suivrait.
 */
function depasseSeuil(activite, valeur) {
    return statutMicro({ activite, anciennete: "etablie", caN1: valeur, caN2: valeur }).sortie;
}

/** Un bloc de verdict, avec son icône et son ton. */
function Verdict({ icone, ton, titre, children }) {
    return (
        <div
            className={`rounded-xl p-5 border ${
                ton === "alerte"
                    ? "border-amber-500/50 bg-amber-500/[0.06]"
                    : ton === "neutre"
                      ? "border-gray-200/70 dark:border-white/10"
                      : "border-primary/40 bg-primary/[0.06]"
            }`}
        >
            <div className="flex items-center gap-2.5">
                <SolarIcon
                    name={icone}
                    className={`w-6 h-6 shrink-0 ${
                        ton === "alerte"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-400 dark:text-lightBlue/60"
                    }`}
                />
                <div className="font-bold dark:text-white leading-snug">{titre}</div>
            </div>
            {children}
        </div>
    );
}

/**
 * Une année lue contre son plafond : libellé, état et montant.
 *
 * Le triangle d'alerte n'apparaît que si le régime cesse : un dépassement
 * isolé ne fait sortir de rien, et le montrer en danger ferait peur pour rien.
 */
function LigneAnnee({ libelle, etat, valeur, depasse, alerte, lang }) {
    return (
        <div className="flex justify-between items-center gap-4 py-2.5 border-b border-gray-200/70 dark:border-white/10 last:border-0">
            <div className="flex items-center gap-2.5 min-w-0">
                <SolarIcon
                    name={
                        !depasse
                            ? "check-circle-linear"
                            : alerte
                              ? "danger-triangle-linear"
                              : "info-circle-linear"
                    }
                    className={`w-5 h-5 shrink-0 ${
                        depasse && alerte
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-400 dark:text-lightBlue/60"
                    }`}
                />
                <div className="min-w-0">
                    <div className="text-sm dark:text-white">{libelle}</div>
                    {etat && (
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">
                            {etat}
                        </div>
                    )}
                </div>
            </div>
            <div className="text-sm font-semibold tabular-nums dark:text-white shrink-0">
                {euros(valeur, lang)}
            </div>
        </div>
    );
}

/** La lecture d'une année mixte : la phrase qui dit laquelle des deux conditions casse. */
function motifMixte(s, t, lang) {
    const plafonds = {
        global: euros(PLAFOND_MIXTE.global, lang),
        services: euros(PLAFOND_MIXTE.services, lang),
    };
    if (s.conforme) return t.mixteAnneeOk;
    if (s.servicesDepasse && s.globalDepasse) return remplir(t.mixteAnneeDeux, plafonds);
    if (s.servicesDepasse) return remplir(t.mixteAnneeServices, plafonds);
    return remplir(t.mixteAnneeGlobal, plafonds);
}

/** Une année mixte sur le résultat : son total, ses deux parts, et sa lecture. */
function BlocAnneeMixte({ titre, s, vente, services, alerte, t, lang }) {
    return (
        <div>
            <LigneAnnee
                libelle={titre}
                etat={remplir(t.mixteDont, {
                    vente: euros(vente, lang),
                    services: euros(services, lang),
                })}
                valeur={s.global}
                depasse={!s.conforme}
                alerte={alerte}
                lang={lang}
            />
            <p
                className={`text-sm mt-2 mb-0 ${
                    !s.conforme && alerte
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-gray-600 dark:text-lightBlue"
                }`}
            >
                {motifMixte(s, t, lang)}
            </p>
        </div>
    );
}

export default function SimulateurPlafonds({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;
    // La page passe toujours `aujourdhui` ; le repli figé n'existe que pour un
    // rendu hors page (tests, aperçus), sans jamais lire l'horloge ici : un
    // `new Date()` côté composant casserait l'hydratation.
    const reference = aujourdhui || "2026-09-06T00:00:00Z";
    const annee = useMemo(() => new Date(reference).getUTCFullYear(), [reference]);

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [anciennete, setAnciennete] = useState(dossier.anciennete ?? null);
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [dateDebut, setDateDebut] = useState(dossier.dateDebut ?? `${annee}-01-01`);
    const [caAnDernier, setCaAnDernier] = useState(dossier.caAnDernier ?? 60000);
    const [caCetteAnnee, setCaCetteAnnee] = useState(dossier.caCetteAnnee ?? 50000);
    const [venteN1, setVenteN1] = useState(80000);
    const [servicesN1, setServicesN1] = useState(40000);
    const [venteN, setVenteN] = useState(80000);
    const [servicesN, setServicesN] = useState(40000);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "anciennete",
        () => {
            setAnciennete(null);
            setActivite(null);
            setDateDebut(`${annee}-01-01`);
            setCaAnDernier(60000);
            setCaCetteAnnee(50000);
            setVenteN1(80000);
            setServicesN1(40000);
            setVenteN(80000);
            setServicesN(40000);
        },
        PHASES,
        // Même aiguillage que versChiffres : la date en première année, rien
        // en deuxième, les deux années ensuite. L'activité mixte ne vient
        // jamais du dossier, ses parts non plus.
        reprendre([
            { etape: "anciennete", connu: dossier.anciennete !== undefined },
            { etape: "activite", connu: dossier.activite !== undefined },
            ...(dossier.activite === "cipav" ? [{ etape: "cipav", connu: true }] : []),
            ...(dossier.anciennete === "premiere"
                ? [{ etape: "dateDebut", connu: dossier.dateDebut !== undefined }]
                : dossier.anciennete === "etablie"
                  ? [
                        { etape: "caN1", connu: dossier.caAnDernier !== undefined },
                        { etape: "caN", connu: dossier.caCetteAnnee !== undefined },
                    ]
                  : []),
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier({ anciennete, activite, dateDebut, caAnDernier, caCetteAnnee }, etape === "resultat");

    const mixte = activite === "mixte";
    const cipav = activite === "cipav";
    // En activité mixte du régime général, seul `acquis` est lu ici : la règle
    // des deux années s'y applique condition par condition, avec statutMixte,
    // plus bas. Un affilié Cipav passe par la voie simple, sur son total.
    const activiteLib = mixte || !activite ? "services" : activite;

    // Verdict sur le 1er janvier prochain : la plus récente des deux années
    // regardées (caN1 pour la lib) est cette année, la plus ancienne (caN2)
    // est l'an dernier.
    const statut = useMemo(
        () =>
            statutMicro({
                activite: activiteLib,
                anciennete,
                caN1: caCetteAnnee,
                caN2: caAnDernier,
            }),
        [activiteLib, anciennete, caCetteAnnee, caAnDernier],
    );
    const seuil = statut.seuil;

    const prorata = useMemo(() => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateDebut)) return null;
        if (Number(dateDebut.slice(0, 4)) !== annee) return null;
        const iso = `${dateDebut}T00:00:00Z`;
        const simple = plafondProratise(seuil, iso);
        if (Number.isNaN(simple.jours)) return null;
        // La lib divise par 365 : une année bissextile entière donnerait un
        // repère au-dessus du plafond légal, que l'écran ne doit pas montrer.
        const borner = (p, plafond) => ({ ...p, plafond: Math.min(p.plafond, plafond) });
        return {
            simple: borner(simple, seuil),
            global: borner(plafondProratise(PLAFOND_MIXTE.global, iso), PLAFOND_MIXTE.global),
            services: borner(
                plafondProratise(PLAFOND_MIXTE.services, iso),
                PLAFOND_MIXTE.services,
            ),
            // Une date à venir est admise : les jours sont alors prévus, pas
            // vécus, et les libellés le disent.
            prevu: joursAvant(dateDebut, reference) > 0,
        };
    }, [dateDebut, annee, seuil, reference]);
    const existence = prorata && (prorata.prevu ? t.existencePrevus : t.existence);

    // La conjonction des deux années vit dans la lib depuis qu'un harnais la
    // tient : le composant ne compose plus la règle, il l'affiche.
    const deuxAns = useMemo(
        () =>
            statutMixteSurDeuxAns({
                n1: { caVente: venteN1, caServices: servicesN1 },
                n: { caVente: venteN, caServices: servicesN },
            }),
        [venteN1, servicesN1, venteN, servicesN],
    );
    const n1 = deuxAns.n1;
    const n = deuxAns.n;

    const plafonds = {
        global: euros(PLAFOND_MIXTE.global, lang),
        services: euros(PLAFOND_MIXTE.services, lang),
    };
    const maximum = Math.ceil((seuil * 1.6) / 1000) * 1000;
    const formater = (v) => euros(v, lang);
    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    // Créé cette année : la date suffit. Créé l'an dernier : rien à calculer,
    // le régime est acquis. Deux ans ou plus : les années regardées, une par
    // écran, en deux parts quand l'activité est mixte.
    const versChiffres = (cle) => {
        if (anciennete === "premiere") aller("dateDebut");
        else if (anciennete === "deuxieme") aller("resultat");
        else aller(cle === "mixte" ? "mixteVenteN1" : "caN1");
    };

    if (etape === "anciennete") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.anciennete}
                titre={t.qAnciennete}
                aide={t.qAncienneteAide}
                choix={["premiere", "deuxieme", "etablie"].map((cle) => ({
                    icone: {
                        premiere: "rocket-2-linear",
                        deuxieme: "calendar-linear",
                        etablie: "history-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setAnciennete(cle);
                        aller("activite");
                    },
                }))}
            />
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={remplir(t.qActiviteAide, {
                    vente: euros(PLAFONDS.vente, lang),
                    services: euros(PLAFONDS.services, lang),
                })}
                choix={["vente", "services", "mixte"].map((cle) => ({
                    icone: {
                        vente: "sale-linear",
                        services: "pen-2-linear",
                        mixte: "routing-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setActivite(cle);
                        // Le plafond d'une activité mixte dépend de la caisse :
                        // un affilié Cipav n'a qu'une condition, sur son total.
                        if (cle === "mixte") aller("cipav");
                        else versChiffres(cle);
                    },
                }))}
            />
        );
    }

    if (etape === "cipav") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.cipav}
                titre={t.qCipav}
                aide={remplir(t.qCipavAide, plafonds)}
                choix={[
                    {
                        icone: "routing-linear",
                        titre: t.cipavNon,
                        detail: t.cipavNonDetail,
                        onClick: () => {
                            setActivite("mixte");
                            versChiffres("mixte");
                        },
                    },
                    {
                        icone: "medal-ribbon-star-linear",
                        titre: t.cipavOui,
                        detail: t.cipavOuiDetail,
                        onClick: () => {
                            setActivite("cipav");
                            versChiffres("cipav");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "dateDebut") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.dateDebut}
                titre={t.qDate}
                aide={t.qDateAide}
            >
                <ChampDate
                    valeur={dateDebut}
                    poser={setDateDebut}
                    min={`${annee}-01-01`}
                    max={`${annee}-12-31`}
                    className="w-full sm:w-72 px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                />
                <p
                    className={`text-sm mt-3 mb-0 ${
                        prorata
                            ? "text-gray-600 dark:text-lightBlue"
                            : "text-amber-600 dark:text-amber-400"
                    }`}
                >
                    {!prorata
                        ? remplir(t.dateHorsAnnee, { annee })
                        : mixte
                          ? remplir(t.prorataMixteDirect, {
                                global: euros(prorata.global.plafond, lang),
                                services: euros(prorata.services.plafond, lang),
                                jours: prorata.global.jours,
                                existence,
                                annee,
                            })
                          : remplir(t.prorataDirect, {
                                plafond: euros(prorata.simple.plafond, lang),
                                seuil: euros(seuil, lang),
                                jours: prorata.simple.jours,
                                existence,
                                annee,
                            })}
                </p>
                <p className="text-sm font-semibold dark:text-white mt-2 mb-0">
                    {t.prorataCapital}
                </p>
                {prorata && (
                    <Avancer onClick={() => aller("resultat")}>{t.voirResultat}</Avancer>
                )}
            </Ecran>
        );
    }

    if (etape === "caN1" || etape === "caN") {
        const anDernier = etape === "caN1";
        const valeur = anDernier ? caAnDernier : caCetteAnnee;
        const depasse = depasseSeuil(activiteLib, valeur);
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                titre={anDernier ? t.qCaN1 : t.qCaN}
                aide={anDernier ? t.qCaN1Aide : t.qCaNAide}
            >
                <Curseur
                    libelle={
                        anDernier
                            ? cipav
                                ? t.caTotalN1
                                : t.caN1
                            : cipav
                              ? t.caTotalN
                              : t.caN
                    }
                    aide={t.caAide}
                    valeur={valeur}
                    poser={anDernier ? setCaAnDernier : setCaCetteAnnee}
                    maximum={maximum}
                    pas={1000}
                    formater={formater}
                />
                <p
                    className={`text-sm mt-3 mb-0 ${
                        depasse
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-600 dark:text-lightBlue"
                    }`}
                >
                    {remplir(
                        anDernier
                            ? depasse
                                ? t.surSeuilN1
                                : t.sousSeuilN1
                            : depasse
                              ? t.surSeuilN
                              : t.sousSeuilN,
                        { seuil: euros(seuil, lang) },
                    )}
                </p>
                <Avancer onClick={() => aller(anDernier ? "caN" : "resultat")}>
                    {anDernier ? t.continuer : t.voirResultat}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "mixteVenteN1" || etape === "mixteVenteN") {
        const anDernier = etape === "mixteVenteN1";
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                titre={anDernier ? t.qMixteVenteN1 : t.qMixteVenteN}
                aide={remplir(anDernier ? t.qMixteVenteN1Aide : t.qMixteVenteNAide, plafonds)}
            >
                <Curseur
                    libelle={t.partVente}
                    aide={t.caAide}
                    valeur={anDernier ? venteN1 : venteN}
                    poser={anDernier ? setVenteN1 : setVenteN}
                    maximum={PLAFOND_MIXTE.global}
                    pas={1000}
                    formater={formater}
                />
                <Avancer onClick={() => aller(anDernier ? "mixteServicesN1" : "mixteServicesN")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "mixteServicesN1" || etape === "mixteServicesN") {
        const anDernier = etape === "mixteServicesN1";
        const s = anDernier ? n1 : n;
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                titre={anDernier ? t.qMixteServicesN1 : t.qMixteServicesN}
                aide={remplir(anDernier ? t.qMixteServicesN1Aide : t.qMixteServicesNAide, plafonds)}
            >
                <Curseur
                    libelle={t.partServices}
                    aide={t.caAide}
                    valeur={anDernier ? servicesN1 : servicesN}
                    poser={anDernier ? setServicesN1 : setServicesN}
                    maximum={PLAFOND_MIXTE.global}
                    pas={1000}
                    formater={formater}
                />
                <p
                    className={`text-sm mt-3 mb-0 ${
                        s.conforme
                            ? "text-gray-600 dark:text-lightBlue"
                            : "text-amber-600 dark:text-amber-400"
                    }`}
                >
                    {motifMixte(s, t, lang)}
                </p>
                {/*
                  Cette année est toujours demandée, même quand l'an dernier
                  est conforme : elle ne change pas le verdict de l'an prochain,
                  mais c'est elle qui dit ce qui se joue l'année d'après.
                */}
                <Avancer onClick={() => aller(anDernier ? "mixteVenteN" : "resultat")}>
                    {anDernier ? t.continuer : t.voirResultat}
                </Avancer>
            </Ecran>
        );
    }

    const prochaine = annee + 1;
    const suivante = annee + 2;
    const seuilTexte = euros(seuil, lang);
    const dates = { seuil: seuilTexte, prochaine, suivante };
    const sortie = mixte ? deuxAns.sortie : statut.sortie;

    let verdict;
    if (statut.acquis) {
        verdict = (
            <div className="space-y-3">
                <Verdict icone="check-circle-linear" ton="calme" titre={t.acquisTitre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                        {anciennete === "premiere" ? t.acquisTexte : t.deuxiemeTexte}
                    </p>
                </Verdict>

                {anciennete === "premiere" && prorata && (
                    <div className="rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                        <div className="flex items-center gap-2.5">
                            <SolarIcon
                                name="target-linear"
                                className="w-6 h-6 shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.prorataTitre}
                            </div>
                        </div>
                        <div className="text-3xl font-bold tabular-nums dark:text-white mt-3">
                            <Montant
                                valeur={mixte ? prorata.global.plafond : prorata.simple.plafond}
                                lang={lang}
                                reduit={reduit}
                            />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {mixte
                                ? remplir(t.prorataMixteTexte, {
                                      services: euros(prorata.services.plafond, lang),
                                      jours: prorata.global.jours,
                                      existence,
                                      annee,
                                  })
                                : remplir(t.prorataTexte, {
                                      seuil: seuilTexte,
                                      jours: prorata.simple.jours,
                                      existence,
                                      annee,
                                  })}
                        </p>
                    </div>
                )}

                {anciennete === "deuxieme" && (
                    <div className="rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                        <div className="flex items-center gap-2.5">
                            <SolarIcon
                                name="target-linear"
                                className="w-6 h-6 shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.plafondTitre}
                            </div>
                        </div>
                        <div className="text-3xl font-bold tabular-nums dark:text-white mt-3">
                            <Montant
                                valeur={mixte ? PLAFOND_MIXTE.global : seuil}
                                lang={lang}
                                reduit={reduit}
                            />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {mixte ? remplir(t.plafondMixteTexte, plafonds) : t.plafondTexte}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.deuxiemeProrata}
                        </p>
                    </div>
                )}

                {mixte && (
                    <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 px-1">
                        {remplir(t.mixteRappel, plafonds)}
                    </p>
                )}
            </div>
        );
    } else if (!mixte) {
        const depasseN1 = depasseSeuil(activiteLib, caAnDernier);
        const depasseN = depasseSeuil(activiteLib, caCetteAnnee);
        const [avantMarge, apresMarge] = t.resteMarge.split("{marge}");
        verdict = (
            <div className="space-y-4">
                {sortie ? (
                    <Verdict
                        icone="danger-triangle-linear"
                        ton="alerte"
                        titre={remplir(t.sortieTitre, dates)}
                    >
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.sortieTexte, dates)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieSocial}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieReel}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieRetour}
                        </p>
                        <p className="text-sm font-semibold dark:text-white mt-2 mb-0">
                            {remplir(t.sortieProvisoire, dates)}
                        </p>
                    </Verdict>
                ) : statut.depassements === 1 ? (
                    <Verdict icone="info-circle-linear" ton="neutre" titre={t.unSeulTitre}>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(depasseN1 ? t.unSeulN1Texte : t.unSeulNTexte, dates)}
                        </p>
                        {depasseN && (
                            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 mb-0">
                                {remplir(t.unSeulNAttention, dates)}
                            </p>
                        )}
                    </Verdict>
                ) : (
                    <Verdict icone="check-circle-linear" ton="calme" titre={t.resteTitre}>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.resteTexte, dates)}
                        </p>
                        {statut.marge > 0 && (
                            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                                {avantMarge}
                                <Montant valeur={statut.marge} lang={lang} reduit={reduit} />
                                {apresMarge}
                            </p>
                        )}
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.resteSuivant}
                        </p>
                    </Verdict>
                )}

                <div>
                    <LigneAnnee
                        libelle={t.anDernier}
                        etat={depasseN1 ? (sortie ? t.dessus : t.dessusIsole) : t.sous}
                        valeur={caAnDernier}
                        depasse={depasseN1}
                        alerte={sortie}
                        lang={lang}
                    />
                    <LigneAnnee
                        libelle={t.cetteAnnee}
                        etat={depasseN ? (sortie ? t.dessus : t.dessusIsole) : t.sous}
                        valeur={caCetteAnnee}
                        depasse={depasseN}
                        alerte={sortie}
                        lang={lang}
                    />
                </div>

                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0 px-1">
                    {t.provisoire}
                </p>
            </div>
        );
    } else {
        // Même règle qu'en activité simple, condition par condition : le régime
        // ne cesse que si l'an dernier ET cette année sortent des conditions.
        // statutMixteSurDeuxAns la porte, le composant ne fait que l'afficher.
        verdict = (
            <div className="space-y-4">
                {sortie ? (
                    <Verdict
                        icone="danger-triangle-linear"
                        ton="alerte"
                        titre={remplir(t.sortieTitre, dates)}
                    >
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.sortieMixteTexte, dates)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieSocial}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieReel}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {t.sortieRetour}
                        </p>
                        <p className="text-sm font-semibold dark:text-white mt-2 mb-0">
                            {t.sortieMixteProvisoire}
                        </p>
                    </Verdict>
                ) : !n1.conforme ? (
                    <Verdict icone="info-circle-linear" ton="neutre" titre={t.unSeulTitre}>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.unSeulMixteN1Texte, dates)}
                        </p>
                    </Verdict>
                ) : !n.conforme ? (
                    <Verdict icone="info-circle-linear" ton="neutre" titre={t.unSeulTitre}>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.unSeulMixteNTexte, dates)}
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 mb-0">
                            {remplir(t.unSeulMixteAttention, dates)}
                        </p>
                    </Verdict>
                ) : (
                    <Verdict icone="check-circle-linear" ton="calme" titre={t.resteTitre}>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.resteMixteTexte, { ...plafonds, ...dates })}
                        </p>
                    </Verdict>
                )}

                <div className="space-y-4">
                    <BlocAnneeMixte
                        titre={t.anDernier}
                        s={n1}
                        vente={venteN1}
                        services={servicesN1}
                        alerte={sortie}
                        t={t}
                        lang={lang}
                    />
                    <BlocAnneeMixte
                        titre={t.cetteAnnee}
                        s={n}
                        vente={venteN}
                        services={servicesN}
                        alerte={sortie}
                        t={t}
                        lang={lang}
                    />
                </div>

                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0 px-1">
                    {t.provisoire}
                </p>
            </div>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">{verdict}</div>

            {cipav && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0 px-1">
                    {remplir(t.cipavRappel, { seuil: seuilTexte })}
                </p>
            )}

            <ul className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 space-y-1.5 list-disc pl-4">
                {t.rappels.map((x) => (
                    <li key={x}>{remplir(x, { tva: euros(SEUILS_TVA.services.base, lang) })}</li>
                ))}
            </ul>

            <button
                type="button"
                onClick={retour}
                className="w-full text-center mt-6 text-sm text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
            >
                ← {t.modifier}
            </button>
            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
